<template>
  <div class="flex items-center justify-between gap-2 pb-2 print:hidden" data-cy="bulk-action-bar">
    <div class="flex items-center gap-3">
      <LlStatusBadge data-cy="selection-count-badge">
        {{ selectedIds.length }} {{ matterTerm }}{{ selectedIds.length === 1 ? '' : 's' }} selected
      </LlStatusBadge>
      <LlSecondaryButton
        variant="text"
        data-cy="clear-selection-button"
        @click="$emit('clear')"
      >
        Clear
      </LlSecondaryButton>
    </div>

    <div class="flex gap-2">
      <LlSecondaryButton
        v-if="canChangeResponsible"
        variant="outlined"
        color="primary"
        prepend-icon="mdi-account-switch"
        :loading="bulkOperationLoading"
        :disabled="bulkOperationLoading"
        data-cy="change-responsible-button"
        @click="$emit('change-responsible')"
      >
        Change Responsible
      </LlSecondaryButton>
      <LlSecondaryButton
        v-if="canApplyRateGroup"
        variant="outlined"
        color="primary"
        prepend-icon="mdi-cash-multiple"
        :loading="bulkOperationLoading"
        :disabled="bulkOperationLoading"
        data-cy="apply-rate-group-button"
        @click="$emit('apply-rate-group')"
      >
        Apply Rate Group
      </LlSecondaryButton>
      <LlSecondaryButton
        v-if="canArchive"
        style="border: 1px solid #9e9e9e"
        :prepend-icon="archived ? 'mdi-archive-arrow-up' : 'mdi-archive-arrow-down'"
        :loading="bulkOperationLoading"
        :disabled="bulkOperationLoading"
        data-cy="archive-button"
        @click="$emit('archive')"
      >
        {{ archived ? 'Unarchive' : 'Archive' }}
      </LlSecondaryButton>
      <v-menu>
        <template #activator="{ props: menuProps }">
          <LlSecondaryButton
            v-bind="menuProps"
            variant="outlined"
            append-icon="mdi-menu-down"
            :disabled="bulkOperationLoading"
            data-cy="more-actions-button"
          >
            More actions
          </LlSecondaryButton>
        </template>
        <v-list density="compact">
          <v-list-item
            prepend-icon="mdi-file-check-outline"
            data-cy="enable-ledes-item"
            @click="$emit('enable-ledes')"
          >
            <v-list-item-title>Enable LEDES</v-list-item-title>
          </v-list-item>
          <v-list-item
            prepend-icon="mdi-file-remove-outline"
            data-cy="disable-ledes-item"
            @click="$emit('disable-ledes')"
          >
            <v-list-item-title>Disable LEDES</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { PropType } from 'vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlStatusBadge from '@/components/LlStatusBadge.vue';

// Purely presentational - the parent (MattersTable.vue) owns the bulk
// operations composable, confirmation dialogs, and process/error state.
// It has to: this bar unmounts via v-if the instant a bulk action clears
// the selection, so any state it owned would disappear before the parent
// could render per-row results from it.
defineProps({
  selectedIds: { type: Array as PropType<string[]>, required: true },
  archived: { type: Boolean, default: false },
  matterTerm: { type: String, default: 'Matter' },
  bulkOperationLoading: { type: Boolean, default: false },
  // Per-action permission gates - the parent already checks permissions.matters.create
  // as the floor for showing this bar at all; these narrow individual buttons further.
  // canApplyRateGroup requires both Rates and Assign - applying a rate group edits both
  // the matter's rates and its MatterShare users, so it counts as "editing rates and
  // users" even though the singular (non-bulk) rate-group path doesn't enforce Assign
  // today. Default to true so any other future consumer that doesn't pass them isn't
  // silently broken.
  canChangeResponsible: { type: Boolean, default: true },
  canApplyRateGroup: { type: Boolean, default: true },
  canArchive: { type: Boolean, default: true },
});

defineEmits([
  'clear',
  'change-responsible',
  'archive',
  'apply-rate-group',
  'enable-ledes',
  'disable-ledes',
]);
</script>
